import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Leaf, Zap, CircleDot, FlaskConical } from 'lucide-react';

const TechniquesSection = () => {
  const { t } = useLanguage();

  const techniques = [
    {
      icon: Leaf,
      title: t('techniques.acupuncture'),
      description: t('techniques.acupunctureDesc'),
    },
    {
      icon: Zap,
      title: t('techniques.electric'),
      description: t('techniques.electricDesc'),
    },
    {
      icon: CircleDot,
      title: t('techniques.cupping'),
      description: t('techniques.cuppingDesc'),
    },
    {
      icon: FlaskConical,
      title: t('techniques.herbal'),
      description: t('techniques.herbalDesc'),
    },
  ];

  return (
    <section 
      id="techniques"
      className="section-padding bg-cream relative overflow-hidden"
      aria-labelledby="techniques-title"
    >
      <div className="container-main">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span 
            className="inline-block font-body text-sm uppercase tracking-widest font-semibold text-sage mb-4"
            data-testid="techniques-caption"
          >
            {t('techniques.caption')}
          </span>
          <h2 
            id="techniques-title"
            className="font-heading text-4xl md:text-5xl font-semibold text-[#1C1C1E] tracking-tight"
            data-testid="techniques-title"
          >
            {t('techniques.title')}
          </h2>
        </div>

        {/* Techniques Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {techniques.map((technique, index) => (
            <div 
              key={index}
              className="group text-center"
              data-testid={`technique-${index}`}
            >
              {/* Icon */}
              <div className="relative mx-auto mb-6">
                <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto group-hover:shadow-xl group-hover:-translate-y-1 transition-shadow transition-transform duration-300">
                  <technique.icon 
                    className="w-9 h-9 text-sage" 
                    aria-hidden="true"
                  />
                </div>
                {/* Decorative ring */}
                <div className="absolute inset-0 w-20 h-20 mx-auto border-2 border-sage/20 rounded-2xl transform rotate-6 group-hover:rotate-12 transition-transform duration-300"></div>
              </div>

              {/* Content */}
              <h3 className="font-heading text-lg font-semibold text-[#1C1C1E] mb-3">
                {technique.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {technique.description}
              </p>
            </div>
          ))}
        </div>

        {/* Herbal Remedies Image */}
        <div className="mt-20 relative">
          <div className="relative rounded-2xl overflow-hidden shadow-xl max-w-4xl mx-auto">
            <img 
              src="https://images.unsplash.com/photo-1614741855162-aeb089f1589d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHx0cmFkaXRpb25hbCUyMGNoaW5lc2UlMjBkaWNpbmUlMjBoZXJiYWwlMjB0ZWElMjBpbmdyZWRpZW50cyUyMHdvb2QlMjB0YWJsZXxlbnwwfHx8fDE3NzEzMDAwMjF8MA&ixlib=rb-4.1.0&q=85"
              alt="Traditional herbal medicine ingredients and remedies used at Healing Clinic"
              className="w-full h-64 md:h-80 object-cover"
              data-testid="techniques-herbal-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/40 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechniquesSection;
